<template>
  <v-container fluid>
    <v-card flat>
      <v-toolbar >
        <v-icon left>post_add</v-icon>
        <v-toolbar-title class="font-weight-light">Alta Masiva de Notas de Crédito</v-toolbar-title>
      </v-toolbar>

      <v-card-text>

        <!-- Filtros -->
        <v-card outlined class="mb-3">
          <v-card-title class="subtitle-2 font-weight-medium pb-0">
            <v-icon left small>filter_list</v-icon>Filtros
          </v-card-title>
          <v-card-text>
            <v-row dense align="center">
              <v-col cols="12" sm="4">
                <v-autocomplete
                  v-model="filtro.empresaId"
                  :items="empresas"
                  item-text="nombre"
                  item-value="id"
                  label="Empresa"
                  placeholder="Seleccione una empresa"
                  prepend-icon="business"
                  clearable
                  dense
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="filtro.montoMinimoCuota"
                  label="Máximo saldo cuota"
                  type="number"
                  min="0"
                  prepend-icon="attach_money"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="filtro.montoMinimoPunitorios"
                  label="Máximo saldo punitorios"
                  type="number"
                  min="0"
                  prepend-icon="attach_money"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2" class="d-flex align-center">
                <v-btn color="primary" block :loading="loading" @click="buscar">
                  <v-icon left>search</v-icon>Buscar
                </v-btn>
              </v-col>
            </v-row>
            <v-row dense align="center">
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="filtro.vectorMinimo"
                  label="Vector Mora Mínimo"
                  type="number"
                  min="0"
                  prepend-icon="trending_down"
                  dense
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="filtro.vectorMaximo"
                  label="Vector Mora Máximo"
                  type="number"
                  min="0"
                  prepend-icon="trending_up"
                  dense
                  clearable
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Tabla de resultados -->
        <v-card outlined v-if="primeraBusquedaRealizada">
          <v-toolbar dense flat color="grey lighten-4">
            <v-icon left small>list</v-icon>
            <span class="subtitle-2">Resultados</span>
            <v-spacer></v-spacer>
            <v-chip v-if="selectedItems.length > 0" small color="blue darken-1" dark class="mr-2">
              {{ selectedItems.length }} seleccionado(s)
            </v-chip>
            <v-btn small color="green darken-1" dark @click="exportarResultadosCsv">
              <v-icon left small>get_app</v-icon>Exportar CSV
            </v-btn>
          </v-toolbar>

          <v-data-table
            dense
            :headers="headers"
            :items="records"
            v-model="selectedItems"
            :loading="loading"
            class="elevation-0"
            item-key="id"
            show-select
            :footer-props="footer"
          >
            <template v-slot:no-data>
              <v-alert :value="true" color="grey lighten-3" class="ma-2">
                <v-icon left>info</v-icon>
                No se encontraron créditos con saldo vencido para los filtros indicados
              </v-alert>
            </template>

            <template v-slot:item.nombre="{ item }">
              <span class="caption">{{ item.nombre }}</span>
            </template>
            <template v-slot:item.documento="{ item }">
              <span class="caption">{{ item.documento }}</span>
            </template>
            <template v-slot:item.credito="{ item }">
              <span class="caption font-weight-medium">{{ item.numero }}</span>
            </template>
            <template v-slot:item.saldoCuota="{ item }">
              <span class="caption">{{ item.saldoCuota | currency }}</span>
            </template>
            <template v-slot:item.saldoPunitorio="{ item }">
              <span class="caption">{{ item.saldoPunitorio | currency }}</span>
            </template>
            <template v-slot:item.comentarios="{ item }">
              <span class="caption grey--text">{{ item.vectorMora }}</span>
            </template>
          </v-data-table>
        </v-card>

      </v-card-text>

      <!-- Sección de generación -->
      <v-divider></v-divider>
      <v-card-text class="pb-2">
        <v-row dense align="center">
          <v-col cols="12" sm="3">
            <v-autocomplete
              v-model="command.sucursalId"
              :items="sucursalesFiltradas"
              item-text="nombre"
              item-value="id"
              label="Sucursal *"
              placeholder="Seleccione una"
              prepend-icon="store"
              :no-data-text="filtro.empresaId ? 'Sin sucursales para esta empresa' : 'Seleccione una empresa primero'"
              clearable
              dense
              hide-details
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="5">
            <v-text-field
              v-model="command.comentarios"
              label="Comentarios"
              prepend-icon="comment"
              clearable
              dense
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-row dense justify="end" no-gutters>
              <v-col cols="auto" class="pr-1">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <v-btn
                        color="green darken-1"
                        dark
                        small
                        :loading="savingCuota"
                        :disabled="selectedItems.length === 0"
                        @click="generarNotasCredito('cuota')"
                      >
                        <v-icon left small>receipt</v-icon>
                        Por Cuota
                        <v-badge v-if="selectedItems.length > 0" :content="selectedItems.length" color="white" inline>
                          <template v-slot:badge><span class="green--text darken-1">{{ selectedItems.length }}</span></template>
                        </v-badge>
                      </v-btn>
                    </span>
                  </template>
                  Generar notas de crédito por saldo de cuota
                </v-tooltip>
              </v-col>
              <v-col cols="auto" class="pr-1">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <v-btn
                        color="orange darken-1"
                        dark
                        small
                        :loading="savingPunitorios"
                        :disabled="selectedItems.length === 0"
                        @click="generarNotasCredito('punitorios')"
                      >
                        <v-icon left small>receipt</v-icon>
                        Por Punitorios
                      </v-btn>
                    </span>
                  </template>
                  Generar notas de crédito por saldo de punitorios
                </v-tooltip>
              </v-col>
              <v-col cols="auto">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">
                      <v-btn
                        color="blue darken-2"
                        dark
                        small
                        :loading="savingAmbos"
                        :disabled="selectedItems.length === 0"
                        @click="generarNotasCredito('ambos')"
                      >
                        <v-icon left small>done_all</v-icon>
                        Ambos
                      </v-btn>
                    </span>
                  </template>
                  Generar notas de crédito por cuota y punitorios
                </v-tooltip>
              </v-col>
            </v-row>
            <v-row dense justify="end" no-gutters class="mt-1">
              <v-col cols="auto">
                <span v-if="selectedItems.length === 0" class="caption grey--text">
                  Seleccione registros para habilitar la generación
                </span>
                <span v-else class="caption blue--text darken-1 font-weight-medium">
                  {{ selectedItems.length }} registro(s) seleccionado(s)
                </span>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Dialog resultados generación -->
    <v-dialog v-model="showResultados" max-width="860" persistent>
      <v-card>
        <v-toolbar dense color="blue darken-2" dark>
          <v-icon left>assignment_turned_in</v-icon>
          <v-toolbar-title class="font-weight-light">Resultado de Generación</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip small dark color="white" class="blue--text darken-2 mr-2">
            {{ resultados.length }} comprobante(s)
          </v-chip>
          <v-btn icon @click="showResultados = false"><v-icon>close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-4 px-4">
          <v-data-table
            dense
            :headers="headersResultados"
            :items="resultados"
            class="elevation-1"
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.autorizado="{ item }">
              <v-chip x-small :color="item.numero >0 ? 'green' : 'red'" dark>
                <v-icon x-small left>{{ item.numero >0 ? 'check' : 'close' }}</v-icon>
                {{ item.numero >0 ? 'OK' : 'Error' }}
              </v-chip>
            </template>
            <template v-slot:item.numero="{ item }">
              <span class="caption font-weight-medium">{{ item.numero || '-' }}</span>
            </template>
            <template v-slot:item.errores="{ item }">
              <span v-if="item.errores" class="caption red--text">{{ item.errores }}</span>
              <span v-else class="caption grey--text">—</span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-row no-gutters align="center" class="px-2">

            <v-col>
              <v-chip small color="green lighten-4" class="mr-2">
                <v-icon small left color="green">check_circle</v-icon>
                {{ resultados.filter(r => r.numero >0).length }} exitosos
              </v-chip>
              <v-chip small color="red lighten-4" v-if="resultados.filter(r => !r.autorizado).length > 0">
                <v-icon small left color="red">error</v-icon>
                {{ resultados.filter(r => !r.numero >0).length }} con error
              </v-chip>
            </v-col>
            <v-col cols="auto" class="d-flex">
              <v-btn color="green darken-1" dark small class="mr-2" @click="exportarCsv">
                <v-icon left small>get_app</v-icon>Exportar CSV
              </v-btn>
              <v-btn color="blue darken-2" dark small @click="showResultados = false">
                <v-icon left small>close</v-icon>Cerrar
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <template v-if="$store.state.debugModeOn">
      Debug Info:
      <p>filtro: {{ filtro }}</p>
      <p>command: {{ command }}</p>
      <p>selectedItems: {{ selectedItems }}</p>
    </template>
  </v-container>
</template>

<script>
import Vue from 'vue'
import { http } from '@/axios'
import { mapGetters, mapMutations } from 'vuex'

export default {
  props: {
    isSubTask: { type: Boolean, default: false },
  },
  data: () => ({
    loading: false,
    savingCuota: false,
    savingPunitorios: false,
    savingAmbos: false,
    primeraBusquedaRealizada: false,
    empresas: [],
    sucursales: [],
    records: [],
    selectedItems: [],
    filtro: {
      empresaId: null,
      montoMinimoCuota: 0,
      montoMinimoPunitorios: 0,
      vectorMinimo: null,
      vectorMaximo: null,
    },
    command: {
      sucursalId: null,
      comentarios: '',
    },
    headers: [
      { text: 'Nombre',      value: 'nombre',        sortable: true  },
      { text: 'Documento',   value: 'documento',      sortable: true  },
      { text: 'Crédito',     value: 'credito',        sortable: true  },
      { text: 'Saldo Cuota', value: 'saldoCuota',     sortable: true  },
      { text: 'Saldo Punit.', value: 'saldoPunitorio', sortable: true },
      { text: 'V.M.', value: 'vectorMora',    sortable: true },
    ],
    footer: {
      showFirstLastPage: true,
      itemsPerPageOptions: [10, 25, 50, 100],
    },
    showResultados: false,
    resultados: [],
    headersResultados: [
      { text: 'Comprobante', value: 'comprobanteDescripcion', sortable: false },
      { text: 'Número',      value: 'numero',                 sortable: false, width: 90  },
      { text: 'Estado',      value: 'autorizado',             sortable: false, width: 90  },
      { text: 'Errores',     value: 'errores',                sortable: false },
    ],
  }),
  computed: {
    ...mapGetters(['user']),
    sucursalesFiltradas() {
      if (!this.filtro.empresaId) return []
      return this.sucursales.filter(s => s.empresaId == this.filtro.empresaId)
    },
  },
  watch: {
    'filtro.empresaId'() {
      this.command.sucursalId = null
    },
  },
  created() {
    this.loadEmpresas()
    this.loadSucursales()
  },
  methods: {
    ...mapMutations([
      'hideActiveTask',
      'hideActiveSubTask',
      'alertSuccess',
      'alertWarning',
      'alertError',
    ]),

    hide(cerrarRelacionados) {
      if (this.isSubTask) this.hideActiveSubTask()
      else this.hideActiveTask(cerrarRelacionados)
    },

    async loadEmpresas() {
      try {
        var result = await http.get('empresas/list/porusuario?usuarioId=' + this.user.id)
        this.empresas = result.list
      } catch (error) {
        this.alertError(error)
      }
    },

    async loadSucursales() {
      try {
        var result = await http.get('sucursales/list/porusuario?usuarioId=' + this.user.id)
        this.sucursales = result.list
      } catch (error) {
        this.alertError(error)
      }
    },

    validarFiltros() {
      if (this.filtro.montoMinimoCuota === null || this.filtro.montoMinimoCuota === '') {
        this.alertWarning('Debe ingresar un monto máximo de saldo de cuota.')
        return false
      }
      if (this.filtro.montoMinimoPunitorios === null || this.filtro.montoMinimoPunitorios === '') {
        this.alertWarning('Debe ingresar un monto máximo de saldo de punitorios.')
        return false
      }
      return true
    },

    async buscar() {
      if (!this.validarFiltros()) return

      this.loading = true
      this.selectedItems = []
      try {
        var url = 'comprobantes/listcreditosparanotacredito' +
            '?empresaId=' + (this.filtro.empresaId || 0) +
            '&saldoCuotaMaximo=' + this.filtro.montoMinimoCuota +
            '&saldoPunitorioMaximo=' + this.filtro.montoMinimoPunitorios

        if (this.filtro.vectorMinimo !== null && this.filtro.vectorMinimo !== '')
          url += '&vectorMinimo=' + this.filtro.vectorMinimo
        if (this.filtro.vectorMaximo !== null && this.filtro.vectorMaximo !== '')
          url += '&vectorMaximo=' + this.filtro.vectorMaximo

        var result = await http.get(url)
        this.records = result.list
        this.primeraBusquedaRealizada = true
        Vue.$log.debug(this.records)
      } catch (error) {
        this.alertError(error)
      }
      this.loading = false
    },

    async generarNotasCredito(tipo) {
      if (this.selectedItems.length === 0) {
        this.alertWarning('Debe seleccionar al menos un registro.')
        return
      }

      if (!this.command.sucursalId) {
        this.alertWarning('Debe seleccionar una sucursal.')
        return
      }

      if (tipo === 'cuota') this.savingCuota = true
      else if (tipo === 'punitorios') this.savingPunitorios = true
      else this.savingAmbos = true

      try {
        var params = {
          tipo: tipo,
          sucursalId: this.command.sucursalId,
          comentarios: this.command.comentarios,
          items: this.selectedItems.map(item => ({
            id: item.id,
            importeCapital: item.saldoCapital,
            importeInteres: item.saldoInteres,
            importeCargo1: item.saldoCargo1,
            importeCargo2: item.saldoCargo2,
            importeCargo3: item.saldoCargo3,
            importeCargo4: item.saldoCargo4,
            importeCargo5: item.saldoCargo5,
            importePunitorio: item.saldoPunitorio,
          })),
        }

        var res = await http.post(
          'comprobantes/generarnotascreditomasivo',
          JSON.stringify(params)
        )

        this.resultados = Array.isArray(res) ? res : (res.list || [])
        this.showResultados = true
        this.selectedItems = []
        this.records = []
        this.primeraBusquedaRealizada = false
      } catch (error) {
        this.alertError(error)
      }

      if (tipo === 'cuota') this.savingCuota = false
      else if (tipo === 'punitorios') this.savingPunitorios = false
      else this.savingAmbos = false
    },

    exportarResultadosCsv() {
      const encabezado = ['Nombre', 'Documento', 'Crédito', 'Saldo Cuota', 'Saldo Punitorios', 'Vector Mora']
      const filas = this.records.map(r => [
        '"' + (r.nombre || '').replace(/"/g, '""') + '"',
        r.documento || '',
        r.numero || '',
        r.saldoCuota || 0,
        r.saldoPunitorio || 0,
        r.vectorMora || '',
      ])

      const contenido = [encabezado.join(';'), ...filas.map(f => f.join(';'))].join('\r\n')
      const blob = new Blob(['\uFEFF' + contenido], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'CreditosSaldoVencido.csv'
      link.click()
      window.URL.revokeObjectURL(url)
    },

    exportarCsv() {
      const encabezado = ['Comprobante', 'Número', 'Estado', 'Errores']
      const filas = this.resultados.map(r => [
        '"' + (r.comprobanteDescripcion || '').replace(/"/g, '""') + '"',
        r.numero || '',
        r.numero > 0 ? 'OK' : 'Error',
        '"' + (r.errores || '').replace(/"/g, '""') + '"',
      ])

      const contenido = [encabezado.join(';'), ...filas.map(f => f.join(';'))].join('\r\n')
      const blob = new Blob(['\uFEFF' + contenido], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'ResultadoNotasCredito.csv'
      link.click()
      window.URL.revokeObjectURL(url)
    },
  },
}
</script>
